<div class="outage-analytics-shell">
  @if (mobileSidebarOpen) {
    <button
      type="button"
      class="outage-analytics-shell__backdrop"
      aria-label="Close sidebar"
      (click)="closeMobileSidebar()"
    ></button>
  }

  <aside
    class="outage-analytics-shell__sidebar"
    [class.outage-analytics-shell__sidebar--collapsed]="sidebarCollapsed"
    [class.outage-analytics-shell__sidebar--mobile-open]="mobileSidebarOpen"
  >
    <app-dashboard-sidebar [collapsed]="sidebarCollapsed" />
  </aside>

  <div class="outage-analytics-shell__content">
    <app-dashboard-header
      [title]="''"
      [subtitle]="''"
      [user]="user"
      [currentTime]="''"
      (menuToggle)="handleMenuToggle()"
    />

    <main class="outage-analytics-page">
      <section class="outage-analytics-page__heading">
        <div class="outage-analytics-page__heading-copy">
          <h2 class="app-title-serif">Outage Analytics</h2>
          <p>View outage summaries, tripped substations, upcoming outages, and percent-wise breakdowns across outage type, category, and technology.</p>
        </div>
      </section>

      <section class="outage-analytics-stats">
        @for (card of getSummaryCards(); track card.label) {
          <article class="outage-stat-card app-card">
            <p class="outage-stat-card__label">{{ card.label }}</p>
            <div class="outage-stat-card__value">{{ card.value }}</div>
            <div class="outage-stat-card__note">{{ card.note }}</div>
          </article>
        }
      </section>

      <section class="outage-analytics-trends">
        <article class="outage-trend-card app-card">
          <div class="outage-trend-card__header">
            <div class="outage-trend-card__heading-copy">
              <p class="outage-trend-card__eyebrow">Most Tripped Units in Particular Duration</p>
              <h3 class="app-title-serif">{{ getMostTrippedTitle() }}</h3>
            </div>

            <div class="outage-trend-card__filters">
              <label class="app-form-field outage-trend-card__filter">
                <span class="app-form-label">Entity</span>
                <select class="app-form-select" [formControl]="trippedEntityControl">
                  <option [ngValue]="null">Overall</option>
                  @for (entity of entities; track trackByEntity($index, entity)) {
                    <option [ngValue]="entity.id">{{ entity.entity_name }}</option>
                  }
                </select>
              </label>

              <label class="app-form-field outage-trend-card__filter">
                <span class="app-form-label">From Date</span>
                <input class="app-form-input" type="date" [formControl]="trippedFromDateControl" />
              </label>

              <label class="app-form-field outage-trend-card__filter">
                <span class="app-form-label">To Date</span>
                <input class="app-form-input" type="date" [formControl]="trippedToDateControl" />
              </label>
            </div>
          </div>

          <div class="outage-trend-card__body">
            <div #mostTrippedChartContainer class="outage-trend-card__chart"></div>
            @if (mostTrippedLoading) {
              <div class="outage-trend-card__loading">Loading most tripped data...</div>
            }
          </div>
        </article>

        <article class="outage-trend-card app-card">
          <div class="outage-trend-card__header">
            <div class="outage-trend-card__heading-copy">
              <p class="outage-trend-card__eyebrow">Upcoming Outage</p>
              <h3 class="app-title-serif">{{ getUpcomingTitle() }}</h3>
            </div>

            <label class="app-form-field outage-trend-card__filter outage-trend-card__filter--single">
              <span class="app-form-label">Entity</span>
              <select class="app-form-select" [formControl]="upcomingEntityControl">
                <option [ngValue]="null">Overall</option>
                @for (entity of entities; track trackByEntity($index, entity)) {
                  <option [ngValue]="entity.id">{{ entity.entity_name }}</option>
                }
              </select>
            </label>
          </div>

          <div class="outage-trend-card__body">
            <div #upcomingChartContainer class="outage-trend-card__chart"></div>
            @if (upcomingLoading) {
              <div class="outage-trend-card__loading">Loading upcoming outages...</div>
            }
          </div>
        </article>
      </section>

      <section class="outage-analytics-percentwise app-card">
        <div class="outage-analytics-percentwise__header">
          <div class="outage-analytics-percentwise__heading-copy">
            <p class="outage-analytics-percentwise__eyebrow">Percentage Wise Outage</p>
            <h3 class="app-title-serif">Outage Type Wise</h3>
          </div>

          <div class="outage-analytics-percentwise__filters" aria-label="Filter outage charts">
            <label class="app-form-field outage-analytics-percentwise__filter">
              <span class="app-form-label">Entity</span>
              <select class="app-form-select" [formControl]="percentwiseEntityControl">
                <option [ngValue]="null">Overall</option>
                @for (entity of entities; track trackByEntity($index, entity)) {
                  <option [ngValue]="entity.id">{{ entity.entity_name }}</option>
                }
              </select>
            </label>

            <label class="app-form-field outage-analytics-percentwise__filter">
              <span class="app-form-label">From Date</span>
              <input class="app-form-input" type="date" [formControl]="chartFromDateControl" />
            </label>

            <label class="app-form-field outage-analytics-percentwise__filter">
              <span class="app-form-label">To Date</span>
              <input class="app-form-input" type="date" [formControl]="chartToDateControl" />
            </label>

            <label class="app-form-field outage-analytics-percentwise__filter">
              <span class="app-form-label">Type</span>
              <select class="app-form-select" [formControl]="chartTypeControl">
                <option value="capacity">Capacity</option>
                <option value="count">Count</option>
              </select>
            </label>
          </div>
        </div>

        <div class="outage-analytics-charts">
          <article class="outage-chart-card app-card">
            <div class="outage-chart-card__header">
              <div>
                <p class="outage-chart-card__eyebrow">Outage Type Wise</p>
                <h3>Outage Type Wise</h3>
              </div>
              <span class="outage-chart-card__chip">{{ getChartTypeLabel() }}</span>
            </div>
            <div #outageTypeChartContainer class="outage-chart-card__body"></div>
          </article>

          <article class="outage-chart-card app-card">
            <div class="outage-chart-card__header">
              <div>
                <p class="outage-chart-card__eyebrow">Category Wise</p>
                <h3>Outage Share by Category</h3>
              </div>
              <span class="outage-chart-card__chip">{{ getChartTypeLabel() }}</span>
            </div>
            <div #categoryChartContainer class="outage-chart-card__body"></div>
          </article>

          <article class="outage-chart-card app-card">
            <div class="outage-chart-card__header">
              <div>
                <p class="outage-chart-card__eyebrow">Technology Wise</p>
                <h3>Generator Type Share</h3>
              </div>
              <span class="outage-chart-card__chip">{{ getChartTypeLabel() }}</span>
            </div>
            <div #technologyChartContainer class="outage-chart-card__body"></div>
          </article>
        </div>
      </section>

      <section class="outage-analytics-table-card app-card">
        <div class="outage-analytics-table-card__header">
          <div class="outage-analytics-table-card__heading-stack">
            <div class="outage-analytics-table-card__heading-copy">
              <p class="outage-analytics-table-card__eyebrow">{{ getTodayOutageTitle() }}</p>
              <h3 class="app-title-serif">{{ getDateLabel() }}</h3>
            </div>

            <div class="outage-analytics-table-card__meta">
              <span class="outage-analytics-table-card__meta-pill">Total {{ outageSummary.total_records }}</span>
              <span class="outage-analytics-table-card__meta-pill">Capacity {{ getTotalCapacityLabel() }}</span>
            </div>
          </div>

          <div class="outage-analytics-table-card__controls">
            <label class="app-form-field outage-analytics-table-card__entity-filter">
              <span class="app-form-label">Entity</span>
              <select class="app-form-select" [formControl]="todayEntityControl">
                <option [ngValue]="null">Overall</option>
                @for (entity of entities; track trackByEntity($index, entity)) {
                  <option [ngValue]="entity.id">{{ entity.entity_name }}</option>
                }
              </select>
            </label>

            <fieldset class="outage-analytics-page__mode-toggle outage-analytics-table-card__date-toggle">
              <legend class="sr-only">Select outage date</legend>
              @for (mode of dateModes; track mode.key) {
                <button
                  type="button"
                  class="outage-analytics-page__mode-button"
                  [class.outage-analytics-page__mode-button--active]="selectedDateMode === mode.key"
                  (click)="selectDateMode(mode.key)"
                >
                  {{ mode.label }}
                </button>
              }
            </fieldset>
          </div>
        </div>

        <div class="outage-analytics-table-card__table-wrap">
          <table class="outage-analytics-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Outage Type</th>
                <th>Generator Type</th>
                <th>Name</th>
                <th>Capacity</th>
                <th>Share</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              @if (isLoading) {
                <tr>
                  <td colspan="7" class="outage-analytics-table__empty">Loading outage analytics...</td>
                </tr>
              } @else if (outageRows.length) {
                @for (row of outageRows; track trackByRow($index, row)) {
                  <tr>
                    <td>{{ row.serial_no }}</td>
                    <td>{{ row.outage_type }}</td>
                    <td>{{ row.generator_type }}</td>
                    <td>{{ row.name }}</td>
                    <td>{{ formatCapacity(row.capacity) }}</td>
                    <td>{{ formatShare(row.share) }}%</td>
                    <td class="outage-analytics-table__reason" [title]="row.reason">{{ row.reason }}</td>
                  </tr>
                }
              } @else {
                <tr>
                  <td colspan="7" class="outage-analytics-table__empty">No outage records found for the selected date.</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (canShowPagination()) {
          <div class="outage-analytics-page__pagination">
            <div class="outage-analytics-page__pagination-summary">
              <span>{{ getPaginationLabel() }}</span>
            </div>

            <div class="outage-analytics-page__pagination-actions">
              <button type="button" class="app-button app-button--secondary outage-analytics-page__pagination-button" [disabled]="pagination.page_no <= 1 || isLoading" (click)="goToPreviousPage()">
                Previous
              </button>

              <div class="outage-analytics-page__pagination-pages" aria-label="Pagination pages">
                @for (page of getPaginationItems(); track $index) {
                  @if (page === -1) {
                    <span class="outage-analytics-page__pagination-ellipsis" aria-hidden="true">...</span>
                  } @else {
                    <button
                      type="button"
                      class="outage-analytics-page__pagination-page"
                      [class.outage-analytics-page__pagination-page--active]="pagination.page_no === page"
                      [disabled]="isLoading"
                      (click)="goToPage(page)"
                    >
                      {{ page }}
                    </button>
                  }
                }
              </div>

              <button type="button" class="app-button app-button--secondary outage-analytics-page__pagination-button" [disabled]="pagination.page_no >= pagination.total_pages || isLoading || pagination.total_pages === 0" (click)="goToNextPage()">
                Next
              </button>
            </div>

            <label class="outage-analytics-page__pagination-size">
              <span class="outage-analytics-page__pagination-size-label">Rows per page</span>
              <select class="app-form-select outage-analytics-page__pagination-select" [formControl]="pageSizeControl">
                @for (size of pageSizeOptions; track size) {
                  <option [ngValue]="size">{{ size }}/Page</option>
                }
              </select>
            </label>
          </div>
        }
      </section>
    </main>
  </div>
</div>
